/*
 * LAND-SEC-007: Quality and accessibility (F3/T3.2)
 * Replaces the two-column bullet list (AUD-006) with a proof matrix and an
 * evidence panel. The panel's numbers come from quality-evidence.json,
 * generated by scripts/collect-test-counts.mjs (plan A6) — never typed here.
 * The strategy/runbook references below link to their GitHub blob view
 * (see Engineering.tsx for the full reasoning).
 */
import qualityEvidence from '../../../data/quality-evidence.json'
import { repoFileUrl } from '../githubRepo'

const PROOF_MATRIX = [
  {
    evidence: 'Keyboard-complete task move',
    demonstrates: 'Drag-and-drop is not the only way to move work',
    destination: 'Live demo — "Move to…" menu on every task card',
  },
  {
    evidence: 'PostgreSQL integration tests',
    demonstrates: 'Constraints and transactions run against a real database, not mocks',
    destination: 'Test matrix',
  },
  {
    evidence: 'Negative authorization tests',
    demonstrates: 'Server-side permission checks and rollback on denied writes',
    destination: 'Security review',
  },
  {
    evidence: 'Daily deterministic reset',
    demonstrates: 'The public demo recovers to a known state every day',
    destination: 'Operations runbook',
  },
]

const generatedDate = new Date(qualityEvidence.generatedAt).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

export function Quality() {
  return (
    <section id="quality" className="landing-section" aria-labelledby="quality-title">
      <div className="landing-section__inner">
        <div className="landing-section__header">
          <h2 id="quality-title" className="landing-section__title">Quality and accessibility</h2>
          <p className="landing-section__subtitle">
            Tested where failure matters: permissions, transactions, keyboard workflows, and
            public-demo recovery.
          </p>
        </div>

        <div className="landing-quality-layout">
          {/* T5.6/A11Y-LAND-012: scroll wrapper so the matrix scrolls
              horizontally on narrow viewports instead of forcing the whole
              page to (same pattern as .landing-roles-scroll in Permissions). */}
          <div className="landing-quality-scroll">
            <table className="landing-quality-matrix">
              <caption className="sr-only">Four categories of test evidence and what each one demonstrates</caption>
              <thead>
                <tr>
                  <th scope="col">Evidence</th>
                  <th scope="col">What it demonstrates</th>
                  <th scope="col">Destination</th>
                </tr>
              </thead>
              <tbody>
                {PROOF_MATRIX.map((row) => (
                  <tr key={row.evidence}>
                    <td>{row.evidence}</td>
                    <td>{row.demonstrates}</td>
                    <td>{row.destination}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="landing-quality-evidence" aria-label="Quality evidence">
            <p className="landing-quality-evidence__label">Quality evidence</p>
            <p className="landing-quality-evidence__figure">
              {qualityEvidence.unitTotal} <span>unit tests</span>
            </p>
            <p className="landing-quality-evidence__figure">
              {qualityEvidence.integrationApi} <span>PostgreSQL integration tests</span>
            </p>
            <p className="landing-quality-evidence__figure">
              {qualityEvidence.e2ePlaywright} <span>Playwright end-to-end tests</span>
            </p>
            <p className="landing-quality-evidence__note">
              WCAG 2.2 AA target + manual keyboard review
            </p>
            <p className="landing-quality-evidence__date">Counted from the test suite on {generatedDate}.</p>
          </aside>
        </div>

        <div className="landing-evidence-ref-group">
          <p className="landing-evidence-ref">
            <span className="landing-evidence-ref__label">Review the testing strategy</span>
            <a
              className="landing-evidence-ref__path"
              href={repoFileUrl('.claude/plans/test-matrix.md')}
              target="_blank"
              rel="noreferrer"
            >
              .claude/plans/test-matrix.md
            </a>
          </p>
          <p className="landing-evidence-ref">
            <span className="landing-evidence-ref__label">Read the operations runbook</span>
            <a
              className="landing-evidence-ref__path"
              href={repoFileUrl('.claude/plans/ph12-operations-runbook.md')}
              target="_blank"
              rel="noreferrer"
            >
              .claude/plans/ph12-operations-runbook.md
            </a>
          </p>
        </div>
      </div>
    </section>
  )
}
